import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { todayString } from '../utils/appDateTime';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DateInput from './ui/date-input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Search, Book, Users, ArrowRightLeft, Pencil, Trash2, RotateCcw, Bell } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import BranchLabel from './ui/BranchLabel';
import useBranchLabels from '../hooks/useBranchLabels';

const emptyBookForm = () => ({
  title: '', author: '', isbn: '', publisher: '', category: '', quantity: '1',
  source: 'purchased', donor_name: '', donation_date: '',
});

const formatLibraryDate = (value) => {
  if (!value || String(value).startsWith('0000-00-00')) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
};

const Library = () => {
  const { user, canManageFeature } = useAuth();
  const canManageLibrary = canManageFeature('library');
  const [books, setBooks] = useState([]);
  const [members, setMembers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [search, setSearch] = useState('');
  const [bookSourceFilter, setBookSourceFilter] = useState('all');
  const [issueFilter, setIssueFilter] = useState('all');
  const [isAddBookOpen, setIsAddBookOpen] = useState(false);
  const [editBook, setEditBook] = useState(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [isIssueOpen, setIsIssueOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberCandidates, setMemberCandidates] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const { toast } = useToast();
  const { showBranchLabels, formatLabel } = useBranchLabels();

  const [bookForm, setBookForm] = useState(emptyBookForm());
  const [memberForm, setMemberForm] = useState({
    user_id: '', membership_date: todayString(),
    expiry_date: '', max_books: '3',
  });
  const [memberEditForm, setMemberEditForm] = useState({
    status: 'active', expiry_date: '', max_books: '3',
  });
  const [issueForm, setIssueForm] = useState({
    book_id: '', member_id: '', issue_date: todayString(), due_date: '',
  });

  useEffect(() => { fetchData(); }, [user?.school_id, bookSourceFilter, issueFilter]);

  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id, bookSourceFilter, issueFilter]);

  const schoolParams = () => {
    const schoolId = user?.school_id;
    const academicYearId = localStorage.getItem(`selected_academic_year_id_${schoolId}`);
    return {
      ...(schoolId ? { school_id: schoolId } : {}),
      ...(academicYearId ? { academic_year_id: academicYearId } : {}),
    };
  };

  const fetchData = async () => {
    try {
      const params = schoolParams();
      const issueParams = { ...params };
      if (issueFilter === 'overdue') issueParams.status = 'overdue';
      else if (issueFilter !== 'all') issueParams.status = issueFilter;

      const bookParams = { ...params };
      if (bookSourceFilter !== 'all') bookParams.source = bookSourceFilter;

      const [booksRes, membersRes, issuesRes] = await Promise.all([
        api.get('/library/books', { params: bookParams }),
        api.get('/library/members', { params }),
        api.get('/library/issues', { params: issueParams }),
      ]);
      setBooks(booksRes.data.data || []);
      setMembers(membersRes.data.data || []);
      setIssues(issuesRes.data.data || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load library data', variant: 'destructive' });
    }
  };

  const handleAddBook = async (e) => {
    e.preventDefault();
    try {
      await api.post('/library/books', { ...bookForm, ...schoolParams(), quantity: parseInt(bookForm.quantity, 10) || 1 });
      toast({ title: 'Success', description: 'Book added successfully' });
      setIsAddBookOpen(false);
      setBookForm(emptyBookForm());
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to add book', variant: 'destructive' });
    }
  };

  const handleUpdateBook = async (e) => {
    e.preventDefault();
    if (!editBook) return;
    try {
      await api.put(`/library/books/${editBook.id}`, {
        ...bookForm,
        quantity: parseInt(bookForm.quantity, 10) || 1,
      });
      toast({ title: 'Success', description: 'Book updated' });
      setEditBook(null);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to update book', variant: 'destructive' });
    }
  };

  const handleDeleteBook = async (book) => {
    if (!window.confirm(`Remove "${book.title}" from inventory?`)) return;
    try {
      await api.delete(`/library/books/${book.id}`);
      toast({ title: 'Deleted', description: 'Book removed' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Cannot delete book', variant: 'destructive' });
    }
  };

  const openEditBook = (book) => {
    setBookForm({
      title: book.title || '',
      author: book.author || '',
      isbn: book.isbn || '',
      publisher: book.publisher || '',
      category: book.category || '',
      quantity: String(book.quantity ?? 1),
      source: book.source || 'purchased',
      donor_name: book.donor_name || '',
      donation_date: book.donation_date || '',
    });
    setEditBook(book);
  };

  const searchUsersForMember = async (q) => {
    setMemberSearch(q);
    if (q.trim().length < 2) {
      setMemberCandidates([]);
      return;
    }
    try {
      const res = await api.get('/library/eligible-users', { params: { ...schoolParams(), search: q.trim() } });
      const list = res.data?.data || [];
      setMemberCandidates(Array.isArray(list) ? list : []);
    } catch {
      setMemberCandidates([]);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberForm.user_id) {
      toast({ title: 'Error', description: 'Select a user', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/library/members', {
        ...memberForm,
        ...schoolParams(),
        user_id: parseInt(memberForm.user_id, 10),
        max_books: parseInt(memberForm.max_books, 10) || 3,
      });
      toast({ title: 'Success', description: 'Member added' });
      setIsAddMemberOpen(false);
      setMemberForm({ user_id: '', membership_date: todayString(), expiry_date: '', max_books: '3' });
      setMemberSearch('');
      setMemberCandidates([]);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to add member', variant: 'destructive' });
    }
  };

  const openEditMember = (member) => {
    setMemberEditForm({
      status: member.status || 'active',
      expiry_date: member.expiry_date || '',
      max_books: String(member.max_books ?? 3),
    });
    setEditMember(member);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!editMember) return;
    try {
      await api.put(`/library/members/${editMember.id}`, {
        ...memberEditForm,
        max_books: parseInt(memberEditForm.max_books, 10) || 3,
      });
      toast({ title: 'Success', description: 'Member updated' });
      setEditMember(null);
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to update member', variant: 'destructive' });
    }
  };

  const handleIssueBook = async (e) => {
    e.preventDefault();
    try {
      await api.post('/library/issue', {
        ...issueForm,
        ...schoolParams(),
        book_id: parseInt(issueForm.book_id, 10),
        member_id: parseInt(issueForm.member_id, 10),
      });
      toast({ title: 'Success', description: 'Book issued' });
      setIsIssueOpen(false);
      setIssueForm({ book_id: '', member_id: '', issue_date: todayString(), due_date: '' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to issue book', variant: 'destructive' });
    }
  };

  const handleReturnBook = async (issue) => {
    try {
      const res = await api.post(`/library/return/${issue.id}`, {});
      const fine = res.data?.data?.fine ?? 0;
      toast({
        title: 'Returned',
        description: fine > 0 ? `Book returned. Fine: ₹${Number(fine).toFixed(2)}` : 'Book returned successfully',
      });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to return book', variant: 'destructive' });
    }
  };

  const handleNotifyOverdue = async () => {
    setNotifying(true);
    try {
      const res = await api.post('/library/notify-overdue', null, { params: schoolParams() });
      const data = res.data?.data || {};
      toast({
        title: 'Notifications sent',
        description: `${data.overdue ?? 0} overdue, ${data.due_soon ?? 0} due soon`,
      });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Failed to send notifications', variant: 'destructive' });
    } finally {
      setNotifying(false);
    }
  };

  const filteredBooks = useMemo(() => books.filter((b) =>
    `${b.title} ${b.author} ${b.isbn}`.toLowerCase().includes(search.toLowerCase()),
  ), [books, search]);

  const availableBooks = books.filter((b) => (b.available_quantity ?? 0) > 0);
  const activeMembers = members.filter((m) => m.status === 'active');
  const overdueCount = issues.filter((i) => i.is_overdue || i.status === 'overdue').length;

  const memberActiveBookIds = useMemo(() => {
    if (!issueForm.member_id) return new Set();
    const memberId = parseInt(issueForm.member_id, 10);
    return new Set(
      issues
        .filter((i) => Number(i.member_id) === memberId && ['issued', 'overdue'].includes(i.status))
        .map((i) => Number(i.book_id)),
    );
  }, [issues, issueForm.member_id]);

  const issueableBooks = useMemo(() => {
    if (!issueForm.member_id) return availableBooks;
    return availableBooks.filter((b) => !memberActiveBookIds.has(Number(b.id)));
  }, [availableBooks, issueForm.member_id, memberActiveBookIds]);

  const handleMemberChangeForIssue = (memberId) => {
    setIssueForm((prev) => {
      const next = { ...prev, member_id: memberId };
      if (next.book_id && memberId) {
        const bookId = parseInt(next.book_id, 10);
        const activeIds = new Set(
          issues
            .filter((i) => Number(i.member_id) === parseInt(memberId, 10) && ['issued', 'overdue'].includes(i.status))
            .map((i) => Number(i.book_id)),
        );
        if (activeIds.has(bookId)) {
          next.book_id = '';
        }
      }
      return next;
    });
  };

  const BookFormFields = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div><Label>Title *</Label><Input value={bookForm.title} onChange={(e) => setBookForm({ ...bookForm, title: e.target.value })} className="bg-muted border-border" required /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Author</Label><Input value={bookForm.author} onChange={(e) => setBookForm({ ...bookForm, author: e.target.value })} className="bg-muted border-border" /></div>
        <div><Label>ISBN</Label><Input value={bookForm.isbn} onChange={(e) => setBookForm({ ...bookForm, isbn: e.target.value })} className="bg-muted border-border" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Category</Label><Input value={bookForm.category} onChange={(e) => setBookForm({ ...bookForm, category: e.target.value })} className="bg-muted border-border" /></div>
        <div><Label>Quantity</Label><Input type="number" min="0" value={bookForm.quantity} onChange={(e) => setBookForm({ ...bookForm, quantity: e.target.value })} className="bg-muted border-border" /></div>
      </div>
      <div>
        <Label>Source</Label>
        <Select value={bookForm.source} onValueChange={(v) => setBookForm({ ...bookForm, source: v })}>
          <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="purchased">Purchased</SelectItem>
            <SelectItem value="donated">Donated</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {bookForm.source === 'donated' && (
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Donor name</Label><Input value={bookForm.donor_name} onChange={(e) => setBookForm({ ...bookForm, donor_name: e.target.value })} className="bg-muted border-border" /></div>
          <div><Label>Donation date</Label><DateInput value={bookForm.donation_date} onChange={(v) => setBookForm({ ...bookForm, donation_date: v })} className="bg-muted border-border" /></div>
        </div>
      )}
      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">{submitLabel}</Button>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="library-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Library</h1>
          <p className="text-muted-foreground">Manage books, members, and circulation</p>
        </div>
        {canManageLibrary && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleNotifyOverdue} disabled={notifying}>
              <Bell className="w-4 h-4 mr-2" /> {notifying ? 'Sending…' : 'Notify overdue'}
            </Button>
            <Dialog open={isIssueOpen} onOpenChange={setIsIssueOpen}>
              <DialogTrigger asChild>
                <Button variant="outline"><ArrowRightLeft className="w-4 h-4 mr-2" /> Issue book</Button>
              </DialogTrigger>
              <DialogContent className="border-border text-foreground">
                <DialogHeader><DialogTitle>Issue book</DialogTitle></DialogHeader>
                <form onSubmit={handleIssueBook} className="space-y-4">
                  <div>
                    <Label>Book</Label>
                    <Select value={issueForm.book_id} onValueChange={(v) => setIssueForm({ ...issueForm, book_id: v })}>
                      <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select book" /></SelectTrigger>
                      <SelectContent>
                        {issueableBooks.map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>{b.title} ({b.available_quantity} available)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!issueForm.member_id && (
                      <p className="text-xs text-muted-foreground mt-1">Select a member first to filter books they already have.</p>
                    )}
                    {issueForm.member_id && issueableBooks.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">No books available — member may already have all issuable titles.</p>
                    )}
                  </div>
                  <div>
                    <Label>Member</Label>
                    <Select value={issueForm.member_id} onValueChange={handleMemberChangeForIssue}>
                      <SelectTrigger className="bg-muted border-border"><SelectValue placeholder="Select member" /></SelectTrigger>
                      <SelectContent>
                        {activeMembers.map((m) => (
                          <SelectItem key={m.id} value={String(m.id)}>{m.first_name} {m.last_name} ({m.member_id})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Issue date</Label><DateInput value={issueForm.issue_date} onChange={(v) => setIssueForm({ ...issueForm, issue_date: v })} className="bg-muted border-border" /></div>
                    <div><Label>Due date (optional)</Label><DateInput value={issueForm.due_date} onChange={(v) => setIssueForm({ ...issueForm, due_date: v })} className="bg-muted border-border" /></div>
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Issue</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={isAddBookOpen} onOpenChange={setIsAddBookOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add book</Button>
              </DialogTrigger>
              <DialogContent className="border-border text-foreground">
                <DialogHeader><DialogTitle>Add book</DialogTitle></DialogHeader>
                <BookFormFields onSubmit={handleAddBook} submitLabel="Add book" />
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-border"><CardContent className="p-6 flex items-center gap-4"><Book className="w-8 h-8 text-blue-500" /><div><p className="text-sm text-muted-foreground">Titles</p><p className="text-2xl font-bold">{books.length}</p></div></CardContent></Card>
        <Card className="border-border"><CardContent className="p-6 flex items-center gap-4"><Users className="w-8 h-8 text-green-500" /><div><p className="text-sm text-muted-foreground">Members</p><p className="text-2xl font-bold">{members.length}</p></div></CardContent></Card>
        <Card className="border-border"><CardContent className="p-6 flex items-center gap-4"><ArrowRightLeft className="w-8 h-8 text-amber-500" /><div><p className="text-sm text-muted-foreground">Active issues</p><p className="text-2xl font-bold">{issues.filter((i) => ['issued', 'overdue'].includes(i.status)).length}</p></div></CardContent></Card>
        <Card className="border-border"><CardContent className="p-6 flex items-center gap-4"><Bell className="w-8 h-8 text-red-500" /><div><p className="text-sm text-muted-foreground">Overdue</p><p className="text-2xl font-bold">{overdueCount}</p></div></CardContent></Card>
      </div>

      <Tabs defaultValue="books" className="space-y-4">
        <TabsList className="bg-card">
          <TabsTrigger value="books">Books</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="issues">Issues</TabsTrigger>
        </TabsList>

        <TabsContent value="books">
          <Card className="border-border">
            <CardHeader className="flex flex-row flex-wrap gap-3 items-center justify-between">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input type="search" placeholder="Search books…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-muted border-border" />
              </div>
              <Select value={bookSourceFilter} onValueChange={setBookSourceFilter}>
                <SelectTrigger className="w-40 bg-muted border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="purchased">Purchased</SelectItem>
                  <SelectItem value="donated">Donated</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {filteredBooks.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No books found</p>
              ) : (
                <div className="grid gap-4">
                  {filteredBooks.map((book) => (
                    <div key={book.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        <Book className="w-8 h-8 text-blue-500 shrink-0" />
                        <div className="min-w-0">
                          <h3 className="font-semibold text-foreground flex items-center gap-2 flex-wrap">
                            {book.title}
                            <BranchLabel name={book.branch_name} show={showBranchLabels} />
                          </h3>
                          <p className="text-sm text-muted-foreground">{book.author} • {book.category || 'Uncategorized'}</p>
                          <p className="text-xs text-muted-foreground">
                            {book.source === 'donated' ? `Donated${book.donor_name ? ` by ${book.donor_name}` : ''}` : 'Purchased'}
                            {' • '}ISBN: {book.isbn || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-foreground font-medium">{book.available_quantity}/{book.quantity}</p>
                        <p className="text-xs text-muted-foreground">{book.issued_quantity ?? (book.quantity - book.available_quantity)} issued</p>
                      </div>
                      {canManageLibrary && (
                        <div className="flex gap-1 shrink-0">
                          <Button size="icon" variant="ghost" onClick={() => openEditBook(book)}><Pencil className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDeleteBook(book)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card className="border-border">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-lg">Library members</CardTitle>
              {canManageLibrary && (
                <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
                  <DialogTrigger asChild><Button size="sm"><Plus className="w-4 h-4 mr-1" /> Add member</Button></DialogTrigger>
                  <DialogContent className="border-border text-foreground">
                    <DialogHeader><DialogTitle>Add library member</DialogTitle></DialogHeader>
                    <form onSubmit={handleAddMember} className="space-y-4">
                      <div>
                        <Label>Search user</Label>
                        <Input value={memberSearch} onChange={(e) => searchUsersForMember(e.target.value)} placeholder="Name or email…" className="bg-muted border-border" />
                        {memberCandidates.length > 0 && (
                          <div className="mt-2 border border-border rounded-md max-h-40 overflow-y-auto">
                            {memberCandidates.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                                onClick={() => {
                                  setMemberForm((prev) => ({ ...prev, user_id: String(u.id) }));
                                  setMemberSearch(`${u.first_name || ''} ${u.last_name || ''}`.trim());
                                  setMemberCandidates([]);
                                }}
                              >
                                {u.first_name} {u.last_name} — {u.email}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Membership date</Label><DateInput value={memberForm.membership_date} onChange={(v) => setMemberForm({ ...memberForm, membership_date: v })} className="bg-muted border-border" /></div>
                        <div><Label>Expiry date</Label><DateInput value={memberForm.expiry_date} onChange={(v) => setMemberForm({ ...memberForm, expiry_date: v })} className="bg-muted border-border" /></div>
                      </div>
                      <div><Label>Max books</Label><Input type="number" min="1" value={memberForm.max_books} onChange={(e) => setMemberForm({ ...memberForm, max_books: e.target.value })} className="bg-muted border-border" /></div>
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Add member</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No members</p>
              ) : (
                <div className="grid gap-4">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div>
                        <h3 className="font-semibold">{member.first_name} {member.last_name}</h3>
                        <p className="text-sm text-muted-foreground">{member.member_id} • max {member.max_books} books</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${member.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{member.status}</span>
                        {canManageLibrary && (
                          <Button size="icon" variant="ghost" onClick={() => openEditMember(member)}><Pencil className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issues">
          <Card className="border-border">
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle className="text-lg">Book issues</CardTitle>
              <Select value={issueFilter} onValueChange={setIssueFilter}>
                <SelectTrigger className="w-36 bg-muted border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {issues.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No issues</p>
              ) : (
                <div className="grid gap-4">
                  {issues.map((issue) => {
                    const isOverdue = issue.is_overdue || issue.status === 'overdue';
                    const canReturn = canManageLibrary && ['issued', 'overdue'].includes(issue.status);
                    return (
                      <div key={issue.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg gap-4">
                        <div>
                          <h3 className="font-semibold flex items-center gap-2 flex-wrap">
                            {issue.book_title}
                            <BranchLabel name={issue.branch_name} show={showBranchLabels} />
                          </h3>
                          <p className="text-sm text-muted-foreground">{issue.first_name} {issue.last_name} ({issue.member_id})</p>
                          <p className="text-xs text-muted-foreground">
                            Due: {formatLibraryDate(issue.due_date)}
                            {issue.return_date ? ` • Returned: ${formatLibraryDate(issue.return_date)}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            isOverdue ? 'bg-red-500/20 text-red-400'
                              : issue.status === 'returned' ? 'bg-green-500/20 text-green-400'
                                : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {isOverdue ? 'overdue' : issue.status}
                          </span>
                          {canReturn && (
                            <Button size="sm" variant="outline" onClick={() => handleReturnBook(issue)}>
                              <RotateCcw className="w-4 h-4 mr-1" /> Return
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(editBook)} onOpenChange={(open) => !open && setEditBook(null)}>
        <DialogContent className="border-border text-foreground">
          <DialogHeader><DialogTitle>Edit book</DialogTitle></DialogHeader>
          <BookFormFields onSubmit={handleUpdateBook} submitLabel="Save changes" />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editMember)} onOpenChange={(open) => !open && setEditMember(null)}>
        <DialogContent className="border-border text-foreground">
          <DialogHeader><DialogTitle>Edit member</DialogTitle></DialogHeader>
          <form onSubmit={handleUpdateMember} className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={memberEditForm.status} onValueChange={(v) => setMemberEditForm({ ...memberEditForm, status: v })}>
                <SelectTrigger className="bg-muted border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Expiry date</Label><DateInput value={memberEditForm.expiry_date} onChange={(v) => setMemberEditForm({ ...memberEditForm, expiry_date: v })} className="bg-muted border-border" /></div>
            <div><Label>Max books</Label><Input type="number" min="1" value={memberEditForm.max_books} onChange={(e) => setMemberEditForm({ ...memberEditForm, max_books: e.target.value })} className="bg-muted border-border" /></div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Library;
